import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import {
  ShieldCheck, Wrench, LineChart, Search, MessageSquare, Home as HomeIcon,
  ArrowRight, Download,
} from "lucide-react";
import { MaskedLines, Reveal, StaggerGroup, staggerItem } from "../components/Motion";
import { CTAButton } from "../components/CTAButton";
import { CTASection } from "../components/CTASection";
import { Seo } from "../components/Seo";
import { trackEvent } from "../components/Analytics";
import { postGuide } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1592947521997-4735c178d012?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1000&h=1250&q=80&ixlib=rb-4.1.0";
const INTERIOR = "https://images.unsplash.com/photo-1630699375895-fe5996d163ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwaW50ZXJpb3IlMjBlbXB0eSUyMHJvb218ZW58MHx8fHwxNzg0Nzc3OTI1fDA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Search, title: "Rigorous Tenant Screening", desc: "Credit, criminal, eviction and income verification on every applicant.", span: "lg:col-span-7" },
  { icon: Wrench, title: "Proactive Maintenance", desc: "24/7 coordination with vetted local vendors before small issues become costly.", span: "lg:col-span-5" },
  { icon: LineChart, title: "Transparent Financial Reporting", desc: "Owner portal with monthly statements, itemized expenses and year-end tax docs.", span: "lg:col-span-5" },
  { icon: MessageSquare, title: "Responsive Communication", desc: "A dedicated manager who answers — same-day, every day.", span: "lg:col-span-7" },
  { icon: ShieldCheck, title: "Local Market Expertise", desc: "10+ years pricing and positioning rentals across Greater Boston.", span: "lg:col-span-7" },
  { icon: HomeIcon, title: "Full-Service Leasing", desc: "Professional marketing, showings and lease execution — units filled fast.", span: "lg:col-span-5" },
];

const manifesto = [
  { n: "01", t: "We treat your asset like an asset.", d: "Every decision is measured against one metric: your net return. No filler fees, no surprises." },
  { n: "02", t: "Stress is optional. Delegate it.", d: "Midnight calls, vendor haggling, late rent — that's our job now, not yours." },
  { n: "03", t: "Massachusetts is our backyard.", d: "From Harvard Square to Brockton, we know the streets, the rents, and the regulations cold." },
];

const stats = [
  { v: "10+", l: "Years in Massachusetts" },
  { v: "$0", l: "Hidden management fees" },
  { v: "21", l: "Avg. days to lease" },
  { v: "99%", l: "Owner retention rate" },
];

const testimonials = [
  { q: "Took the stress out of owning rental property. Very professional and my units are never vacant for long.", a: "Charles C.", r: "Owner, 4 units — Roxbury" },
  { q: "They filled my Cambridge condo in nine days at above asking rent. Genuinely impressed.", a: "Michael Z.", r: "Investor — Cambridge" },
  { q: "Responsive, honest, and they actually understand the local market. A rare combination.", a: "Naomie & Esther C.", r: "Owners — Weymouth" },
];

const GuideForm = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await postGuide(form);
      toast.success("Your Landlord Guide is on its way to your inbox.");
      trackEvent("guide_download", { resource: "landlord_guide" });
      if (res && res.guide_url) window.open(res.guide_url, "_blank", "noopener");
      setForm({ name: "", email: "" });
    } catch {
      toast.error("Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="guide-form">
      <input
        data-testid="guide-name" required placeholder="Full name" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="w-full border border-border bg-background px-4 py-3.5 text-sm focus:border-accent focus:outline-none"
      />
      <input
        data-testid="guide-email" required type="email" placeholder="Email address" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className="w-full border border-border bg-background px-4 py-3.5 text-sm focus:border-accent focus:outline-none"
      />
      <button data-testid="guide-submit" disabled={loading}
        className="group flex w-full items-center justify-center gap-2 bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-60">
        <Download className="h-4 w-4" />
        {loading ? "Sending..." : "Download the Free Guide"}
      </button>
    </form>
  );
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <>
      <Seo title="Full-Service Property Management in Massachusetts" description="Cummings & Co. helps busy Massachusetts landlords maximize returns and minimize stress with tenant screening, maintenance, and transparent reporting." />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden border-b border-border pt-32 lg:pt-40">
        <div className="mx-auto grid max-w-[1400px] items-end gap-10 px-6 pb-16 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:pb-24">
          <div>
            <Reveal>
              <p className="font-serif text-3xl font-medium tracking-tight text-foreground sm:text-4xl">Cummings &amp; Co.</p>
              <span className="overline mt-3 block text-accent">Residential Property Management · Massachusetts</span>
            </Reveal>
            <MaskedLines
              lines={["Maximize returns.", "Minimize stress.", "Own with ease."]}
              delay={0.15}
              className="mt-6 font-serif text-5xl font-light leading-[0.92] tracking-tight text-foreground sm:text-6xl lg:text-7xl"
            />
            <Reveal delay={0.6}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Reliable, full-service management for busy landlords. We handle screening, maintenance, and reporting — you collect the returns.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <CTAButton to="/rental-analysis" variant="accent" testid="hero-rental-analysis-btn">What's My Rental Worth?</CTAButton>
                <CTAButton to="/contact" variant="outline" testid="hero-contact-btn">Book a Consultation</CTAButton>
              </div>
            </Reveal>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="relative aspect-[4/5] overflow-hidden border border-border"
          >
            <motion.img style={{ y, scale }} src={HERO} alt="Urban brick residential property in Cambridge, Massachusetts"
              className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-primary-foreground/20 bg-primary/80 px-5 py-4 backdrop-blur-sm">
              <span className="font-serif text-lg text-primary-foreground">Cambridge, Massachusetts</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-b border-border bg-primary py-5 text-primary-foreground">
        <Marquee speed={40} gradient={false} autoFill>
          {["Maximizing Returns", "Minimizing Stress", "Rigorous Tenant Screening", "Proactive Maintenance", "Transparent Reporting", "Local Market Expertise"].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-serif text-2xl font-light">
              {t} <span className="text-accent">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* STATS */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-border border-x border-border lg:grid-cols-4 lg:px-0">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="px-6 py-12 text-center lg:px-8 lg:py-16">
              <div className="font-serif text-5xl font-light text-accent lg:text-6xl">{s.v}</div>
              <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-28">
          <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <Reveal><span className="overline text-accent">What we do</span></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-tight sm:text-5xl lg:text-6xl">
                  Everything a landlord needs — under one roof.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}><CTAButton to="/services" variant="ghost" testid="services-view-all">View all services</CTAButton></Reveal>
          </div>

          <StaggerGroup className="grid gap-px border border-border bg-border lg:grid-cols-12">
            {services.map((s, i) => (
              <motion.div key={i} variants={staggerItem}
                className={`group bg-background p-8 transition-colors duration-300 hover:bg-muted lg:p-10 ${s.span}`}>
                <s.icon className="h-8 w-8 text-accent" strokeWidth={1.4} />
                <h3 className="mt-6 font-serif text-2xl font-normal">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-32">
          <Reveal><span className="overline text-accent">Our philosophy</span></Reveal>
          <div className="mt-12 divide-y divide-primary-foreground/15 border-y border-primary-foreground/15">
            {manifesto.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="grid gap-6 py-10 lg:grid-cols-[auto_1fr_1.2fr] lg:items-baseline lg:gap-16 lg:py-14">
                  <span className="font-serif text-5xl font-light text-accent lg:text-7xl">{m.n}</span>
                  <h3 className="font-serif text-3xl font-light leading-tight lg:text-4xl">{m.t}</h3>
                  <p className="text-primary-foreground/70">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-28">
          <Reveal><span className="overline text-accent">Trusted by owners</span></Reveal>
          <div className="mt-12 grid gap-px border border-border bg-border md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-background p-8 lg:p-10">
                <p className="font-serif text-2xl font-light leading-snug">"{t.q}"</p>
                <div className="mt-8">
                  <div className="font-medium">{t.a}</div>
                  <div className="text-sm text-muted-foreground">{t.r}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LANDLORD GUIDE LEAD CAPTURE */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] gap-px border-x border-border bg-border lg:grid-cols-2">
          <div className="relative min-h-[360px] overflow-hidden bg-background">
            <img src={INTERIOR} alt="Modern interior" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="bg-background p-8 lg:p-16">
            <span className="overline text-accent">Free Resource</span>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight lg:text-5xl">
              The 2025 Massachusetts Landlord Guide
            </h2>
            <p className="mt-4 text-muted-foreground">
              Screening checklists, MA compliance essentials, pricing frameworks and maintenance systems — the playbook we use every day.
            </p>
            <div className="mt-8 max-w-md"><GuideForm /></div>
            <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <ArrowRight className="h-3 w-3" /> No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
